<template>
  <div class="platform-notice-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">PLATFORM INFORMATION</p>
        <h1>お知らせ配信</h1>
        <p>
          ここで出したお知らせは、<strong>契約しているすべての会社のダッシュボード</strong>に
          同じ内容で出ます。社内向けの掲示ではありません。
        </p>
      </div>
      <CButton
        color="secondary"
        variant="outline"
        :disabled="loading || saving"
        @click="load"
      >
        再読込
      </CButton>
    </header>

    <!--
      **掲載期限が必須である理由を、書く人に見せる。**
      画面へ直に書いていた頃、1年近く前の知らせが残り続けた。
    -->
    <CAlert color="info" class="notice-alert">
      掲載を終える日は<strong>必ず入れてください。</strong>
      終わりを決めないお知らせは、誰も消さないまま古くなります。
      ずっと出したいものは、期限を延ばして出し直してください。
    </CAlert>
    <!--
      画面上端に出すのは、一覧の読み込みの失敗だけ（2026-09-26）。
      ［出す］［やめる］の結果は、押したカード・行の中に出す。
    -->
    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>

    <CCard class="notice-card">
      <CCardHeader class="section-header">
        <div>
          <strong>新しいお知らせ</strong>
          <small>投稿した時点から、指定した日付の終了時まで表示されます。</small>
        </div>
      </CCardHeader>
      <CCardBody>
        <div class="notice-form">
          <label class="notice-form__field notice-form__field--kind">
            <span>種類</span>
            <select v-model="form.category" class="form-control">
              <option v-for="option in categoryOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
          </label>
          <label class="notice-form__field notice-form__field--body">
            <span>内容（120文字まで）</span>
            <input
              v-model.trim="form.body"
              type="text"
              class="form-control"
              maxlength="120"
              placeholder="9月1日の深夜にメンテナンスを行います。"
            >
          </label>
          <label class="notice-form__field notice-form__field--until">
            <span>掲載を終える日</span>
            <input v-model="form.expires_on" type="date" class="form-control">
          </label>
          <CButton
            color="primary"
            :disabled="saving || !canPublish"
            @click="publish"
          >
            出す
          </CButton>
        </div>
        <!--
          **出したあとに直す手段は置かない**（お勧めで決めたこと 5、2026-09-30）。
          内容の修正と開始日時の指定は足さない。直すなら［やめる］で終えて出し直す。
          押す前に読める場所（入力欄のすぐ下）に書く。
        -->
        <p class="notice-form__note">掲載後の変更はできません。変更する場合は［やめる］で終了後、再投稿してください（掲載期間は最長180日）。</p>
        <ActionResultMessage v-bind="publishResult"/>
      </CCardBody>
    </CCard>

    <CCard class="notice-card">
      <CCardHeader class="section-header">
        <div>
          <strong>出したお知らせ</strong>
          <small>掲載が終了したお知らせも、過去の配信履歴として保持されます。</small>
        </div>
      </CCardHeader>
      <CCardBody>
        <div v-if="loading" class="loading-state"><CSpinner color="primary"/></div>
        <p v-else-if="notices.length === 0" class="empty-state">
          まだ1件も出していません。いまはどの会社のダッシュボードにも、
          お知らせのカードが出ていません。
        </p>
        <table v-else class="notice-table">
          <thead>
            <tr>
              <th>状態</th>
              <th>投稿日</th>
              <th>種類</th>
              <th>内容</th>
              <th>投稿者</th>
              <th>終わり</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="notice in notices" :key="notice.id">
              <td>
                <CBadge :color="isLive(notice) ? 'success' : 'secondary'">
                  {{ isLive(notice) ? '掲載中' : '終了' }}
                </CBadge>
              </td>
              <td class="notice-table__date">{{ day(notice.publishedAt) }}</td>
              <td>{{ categoryLabel(notice.category) }}</td>
              <td>{{ notice.body }}</td>
              <!--
                **投稿者と、［やめる］を押した人を出す**（2026-09-30、束7 の見直し）。運営管理者が2人いると、
                どちらが出したのか・止めたのかを画面から読めなかった。名前はサーバが、見ている人と
                同じ会社の利用者からだけ返す。無ければ「—」。
              -->
              <td class="notice-table__who">{{ notice.createdByName || '—' }}</td>
              <!--
                **入れた日で出す**（2026-09-30、束7）。10/31 と入れたものはサーバが
                11/1 の 0:00 に終わる時刻で持つので、時刻を日付にすると 11/1 と出ていた。
              -->
              <td class="notice-table__date">
                {{ lastDayText(notice) }}
                <small v-if="notice.endedByName" class="notice-table__ended">{{ notice.endedByName }}が［やめる］を実行</small>
              </td>
              <td>
                <CButton
                  v-if="isLive(notice)"
                  size="sm"
                  color="danger"
                  variant="outline"
                  :disabled="saving"
                  @click="end(notice)"
                >
                  やめる
                </CButton>
                <!-- ［やめる］の結果。やめたあともこの行は履歴として残るので、ここに出す。 -->
                <ActionResultMessage v-bind="rowResultOf(notice.id)"/>
              </td>
            </tr>
          </tbody>
        </table>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import moment from 'moment'
import ActionResultMessage from '@/components/ActionResultMessage.vue'
import platformNoticeApi from '@/services/platformNotices'
// 種類の札は、ダッシュボードのお知らせと同じ変換を使う（束18）。
import { NOTICE_CATEGORIES, noticeCategoryLabel } from '@/utils/platformNoticeCategory'

const emptyForm = () => ({
  category: 'SYSTEM',
  body: '',
  /* **既定を入れない。**終わりは、書く人が毎回決める。 */
  expires_on: ''
})

export default {
  name: 'PlatformNotices',
  components: { ActionResultMessage },
  data () {
    return {
      loading: false,
      saving: false,
      /* 一覧の読み込みの失敗だけ（上端に出す）。 */
      errorMessage: '',
      /* ［出す］の結果。「新しいお知らせ」のカードの中に出す。 */
      publishResult: { type: 'success', text: '' },
      /* ［やめる］の結果。キーはお知らせ。その行に出す。 */
      rowResults: {},
      notices: [],
      form: emptyForm()
    }
  },
  computed: {
    /** 種類の選択肢。ダッシュボードの札と同じ表から出す。 */
    categoryOptions () {
      return NOTICE_CATEGORIES
    },

    /** 3つとも埋まっていないと出せない。**期限も含めて。** */
    canPublish () {
      return Boolean(this.form.category)
        && Boolean(this.form.body)
        && Boolean(this.form.expires_on)
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    /**
     * 一覧を読み直す。
     *
     * ［やめる］のあとは quiet で呼ぶ。回転に差し替えると表が消えて、押した行と
     * その結果が一瞬なくなり、画面の位置もずれる。
     * （［再読込］からは押したときの出来事が渡るので、quiet は立たない。）
     */
    async load (options) {
      const quiet = !!(options && options.quiet === true)
      if (!quiet) this.loading = true
      this.errorMessage = ''
      try {
        this.notices = await platformNoticeApi.list()
      } catch (error) {
        /*
         * ［やめる］のあとの読み直し（quiet）で落ちたときは、一覧を空にしない
         * （2026-09-26 監査）。空にすると押した行ごと「お知らせを終了しました。」が
         * 消え、上端の「取得できませんでした」だけが残って、終了できたのか分からない。
         */
        if (!quiet) this.notices = []
        this.errorMessage = this.readMessage(error, 'お知らせを取得できませんでした。画面を再読み込みしてください。')
      } finally {
        if (!quiet) this.loading = false
      }
    },

    /** その行に出す結果。無ければ空（部品は何も出さない）。 */
    rowResultOf (noticeId) {
      return this.rowResults[noticeId] || { type: 'success', text: '' }
    },

    async publish () {
      this.saving = true
      this.publishResult = { type: 'success', text: '' }
      try {
        await platformNoticeApi.publish(this.form)
        this.form = emptyForm()
        this.publishResult = { type: 'success', text: 'お知らせを掲載しました。' }
        await this.load()
      } catch (error) {
        this.publishResult = {
          type: 'danger',
          text: this.readMessage(error, 'お知らせを掲載できませんでした。時間をおいて再度お試しください。')
        }
      } finally {
        this.saving = false
      }
    },

    async end (notice) {
      this.saving = true
      this.$delete(this.rowResults, notice.id)
      try {
        await platformNoticeApi.end(notice.id)
        this.$set(this.rowResults, notice.id, { type: 'success', text: 'お知らせを終了しました。' })
        await this.load({ quiet: true })
      } catch (error) {
        this.$set(this.rowResults, notice.id, {
          type: 'danger',
          text: this.readMessage(error, 'お知らせの掲載を終了できませんでした。時間をおいて再度お試しください。')
        })
      } finally {
        this.saving = false
      }
    },

    /** いま出ているか。**終わりが未来にあるかどうかで見る。** */
    isLive (notice) {
      const until = moment(notice && notice.expiresAt)
      return until.isValid() && until.isAfter(moment())
    },

    day (value) {
      const at = moment(value)
      return at.isValid() ? at.format('YYYY.MM.DD') : ''
    },

    /**
     * 「終わり」の欄。**サーバが返す入れた日（lastDay）だけを使う。**
     *
     * 終わりの時刻（expiresAt）を日付にすると、入れた日の翌日になる（その日の終わりまで
     * 出すため、翌日の 0:00 で持っている）。入れた日を返さない古いサーバのあいだは、
     * 入れていない日を出すより空にする。
     */
    lastDayText (notice) {
      const lastDay = notice && notice.lastDay
      const at = moment(lastDay, 'YYYY-MM-DD', true)
      return at.isValid() ? at.format('YYYY.MM.DD') + 'まで' : ''
    },

    categoryLabel (category) {
      return noticeCategoryLabel(category)
    },

    /**
     * サーバの言い分だけを出す。
     *
     * axios が付ける英語（Network Error など）は画面へ出さない。
     */
    readMessage (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data.message === 'string' && data.message) {
        return data.message
      }
      return fallback
    }
  }
}
</script>

<style scoped>
.platform-notice-page { padding: 8px 0 32px; }

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 20px;
}
.page-header h1 { margin: 0 0 8px; font-size: 24px; }
.page-header p { margin: 0; color: #6f8190; font-size: 13px; max-width: 60ch; }
.eyebrow {
  margin: 0 0 4px;
  color: var(--preika-primary, #1f6fb2);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .16em;
}

.notice-alert { font-size: 13px; }
.notice-card { margin-bottom: 20px; }

.section-header strong { display: block; }
.section-header small { color: #8798a5; }

.notice-form {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
}
.notice-form__field { display: flex; flex-direction: column; gap: 4px; margin: 0; }
.notice-form__field span { color: #6f8190; font-size: 12px; }
.notice-form__field--kind { width: 160px; }
.notice-form__field--body { flex: 1 1 320px; min-width: 240px; }
.notice-form__field--until { width: 180px; }
.notice-form__note { margin: 8px 0 0; color: #6f8190; font-size: 12px; }

.loading-state, .empty-state { padding: 12px 0; color: #6f8190; font-size: 13px; }

/* 幅の広い表は、表の中だけで横に流す。**本文ごと横スクロールさせない。** */
.notice-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.notice-table th, .notice-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #edf2f6;
  text-align: left;
  vertical-align: top;
}
.notice-table th { color: #8798a5; font-size: 12px; font-weight: 700; }
.notice-table__date { white-space: nowrap; font-variant-numeric: tabular-nums; }
.notice-table__who { white-space: nowrap; }
.notice-table__ended { display: block; margin-top: 2px; color: #6f8190; font-size: 12px; }

@media (max-width: 767px) {
  .page-header { flex-direction: column; }
  .notice-form__field--kind,
  .notice-form__field--until { width: 100%; }
}
</style>
